import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Alert,
  Chip,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";
import { useDispatch, useSelector } from "react-redux";
import { updatePmFrame, loadPmPartWeights } from "../actions/ProductionMonitorAction";

const NAVY = "#142c54";

/* -------------------- Helpers -------------------- */
const toYmd = (v) => String(v || "").slice(0, 10);

// Typed digits -> yyyy-MM-dd as you type
const formatDateTyping = (raw) => {
  const d = String(raw || "").replace(/\D/g, "").slice(0, 8);
  if (d.length <= 4) return d;
  if (d.length <= 6) return `${d.slice(0, 4)}-${d.slice(4)}`;
  return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6)}`;
};

const isValidDateText = (t) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t || "")) return false;
  const [y, m, d] = t.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
};

// Frame / OCL No rules (same code is in InsertFrames.js and Editframedialog.js)
//   Frame -24 Port Side | Frame -24 Starboard Side | Frame -24 Center | Frame 12 | Frame -20-300 Center
//   OCL 700 Port Side   | OCL 700 Starboard Side   | OCL 700 Center   | OCL 05

const FRAME_SIDES = [
  { key: "p", label: "Port Side" },
  { key: "s", label: "Starboard Side" },
  { key: "c", label: "Center" },
];

const FRAME_NO_HINT = "Use Frame -24 / OCL 700 + Port Side / Starboard Side / Center";

const SIDE_RE = "(Port Side|Starboard Side|Center)";
const PREFIXES = ["frame", "ocl"];

// frame / FRAME -> Frame, ocl / Ocl -> OCL
const fixFrameNo = (text) =>
  String(text || "")
    .replace(/\bframe(?![a-z])/gi, "Frame")
    .replace(/\bocl(?![a-z])/gi, "OCL");

// While typing: can this text still become a valid value?
const canTypeFrameNo = (text) => {
  const t = String(text || "");
  const lower = t.toLowerCase();
  if (!t) return true;
  if (/^\s/.test(t)) return false; // no leading space
  if (PREFIXES.some((p) => p.startsWith(lower))) return true; // "F", "Fra", "OC"

  const prefix = PREFIXES.find((p) => lower.startsWith(p));
  if (!prefix) return false;

  const rest = lower.slice(prefix.length);
  if (/^ ?-? ?(\d+(-\d+)*-?)?$/.test(rest)) return true; // "Frame -24", "Frame -20-300", "OCL 700"
  const m = /^ ?-? ?\d+(?:-\d+)* (.*)$/.exec(rest); // "OCL 700 Port Si", "Frame -20-300 Cen"
  if (!m) return false;
  return FRAME_SIDES.some((s) => s.label.toLowerCase().startsWith(m[1]));
};

// "frame-24 port side" -> "Frame -24 Port Side", "ocl700 c" -> "OCL 700 Center", "ocl   5" -> "OCL 5"
const normalizeFrameNo = (text) => {
  const t = fixFrameNo(text).trim().replace(/\s+/g, " ");
  const f = new RegExp(`^(Frame|OCL) ?(-)? ?(\\d+(?:-\\d+)*)(?: ${SIDE_RE})?$`, "i").exec(t);
  if (!f) return t;
  const side = f[4] ? FRAME_SIDES.find((s) => s.label.toLowerCase() === f[4].toLowerCase()).label : "";
  return `${f[1]} ${f[2] || ""}${f[3]}${side ? ` ${side}` : ""}`;
};

const isValidFrameNo = (text) => new RegExp(`^(Frame|OCL) -?\\d+(?:-\\d+)*( ${SIDE_RE})?$`).test(normalizeFrameNo(text));

// "Frame -24" / "OCL 700" typed, side not chosen yet -> show Port / Starboard / Center suggestions
const needsFrameSide = (text) => /^(Frame|OCL) ?-? ?\d+(?:-\d+)* ?$/.test(fixFrameNo(text));

// "OCL 700" + "Port Side" -> "OCL 700 Port Side"
const withFrameSide = (text, side) => `${normalizeFrameNo(text)} ${side}`;

// Handle one change in the box. Returns the new value, or null to ignore the key.
//  - p / s / c after the number -> Port Side / Starboard Side / Center (Frame and OCL)
//  - backspace inside a side -> removes the whole side
//  - only Frame.. / OCL.. can be typed
const applyFrameNoTyping = (prev, raw) => {
  const before = String(prev || "");
  const v = fixFrameNo(raw);

  if (v.length < before.length) {
    const full = new RegExp(`^((?:Frame|OCL) ?-? ?\\d+(?:-\\d+)*) ${SIDE_RE}$`).exec(before);
    if (full && v.startsWith(full[1]) && v.length > full[1].length) return `${full[1]} `;
    return v; // deleting is always allowed
  }

  const short = /^((?:Frame|OCL) ?-? ?\d+(?:-\d+)*) ?([psc])$/i.exec(v);
  if (short) {
    const side = FRAME_SIDES.find((s) => s.key === short[2].toLowerCase()).label;
    return withFrameSide(short[1], side);
  }

  return canTypeFrameNo(v) ? v : null;
};

// Build the form from a frame from GetFrameDetails
const formFromFrame = (f) => ({
  iwoNo: String(f?.IwoNo ?? ""),
  componentName: f?.ComponentName || "",
  frameNo: f?.FrameNo || "",
  startDate: toYmd(f?.StartDate),
  endDate: toYmd(f?.EndDate),
  weight: f?.Weight === null || f?.Weight === undefined ? "" : String(f.Weight),
  parts: (f?.Parts || []).map((p, i) => ({
    key: `old-${p.PartId ?? p.partId ?? i}`,
    partId: String(p.PartId ?? p.partId ?? ""),
    partNo: String(p.PartNo ?? p.partNo ?? ""),
    original: String(p.PartNo ?? p.partNo ?? ""),
    removed: false,
  })),
});

let newPartSeq = 0;

/* -------------------- Edit Frame dialog -------------------- */
const EditFrameDialog = ({ open, frame, iwoList, components, frameList = [], onClose, onSaved }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { frameUpdateLoading } = useSelector((state) => state.productionMonitor);

  const [form, setForm] = useState(() => formFromFrame(frame));
  const [error, setError] = useState("");
  const [showErrors, setShowErrors] = useState(false);

  // reset every time a frame is opened
  useEffect(() => {
    if (open) {
      setForm(formFromFrame(frame));
      setError("");
      setShowErrors(false);
    }
  }, [open, frame]);

  const original = useMemo(() => formFromFrame(frame), [frame]);
  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  /* ---- weight = sum of the part weights (read only, same as Insert Frames) ---- */
  const [partWeightMap, setPartWeightMap] = useState(new Map());
  const [partWeightsLoading, setPartWeightsLoading] = useState(false);
  useEffect(() => {
    if (!open || !form.iwoNo) return undefined;
    let alive = true;
    setPartWeightsLoading(true);
    dispatch(loadPmPartWeights(form.iwoNo)).then((res) => {
      if (!alive) return;
      const map = new Map();
      (res.data || []).forEach((p) => {
        if (p.Weight !== null && p.Weight !== undefined) map.set(String(p.PartNo).trim(), Number(p.Weight));
      });
      setPartWeightMap(map);
      setPartWeightsLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [dispatch, open, form.iwoNo]);

  const autoWeight = useMemo(() => {
    const parts = Array.from(new Set(form.parts.filter((p) => !p.removed).map((p) => p.partNo.trim()).filter(Boolean)));
    let sum = 0;
    let found = 0;
    parts.forEach((pn) => {
      if (partWeightMap.has(pn)) {
        sum += partWeightMap.get(pn);
        found += 1;
      }
    });
    return { weight: found > 0 ? String(Math.round(sum * 1000) / 1000) : "", found, total: parts.length };
  }, [form.parts, partWeightMap]);
  // shown weight: the calculated one, or the saved one when this IWO has no part weights
  const shownWeight = autoWeight.weight !== "" ? autoWeight.weight : original.weight;

  const iwoOptions = useMemo(() => {
    const list = [...(iwoList || [])];
    // keep the current IWO selectable even if it is not in the list
    if (form.iwoNo && !list.some((x) => String(x.IwoNo) === form.iwoNo)) list.unshift({ IwoNo: form.iwoNo, EngSpec: "" });
    return list;
  }, [iwoList, form.iwoNo]);

  // another frame of the chosen IWO is already EDGE Grinding (only one per IWO)
  const edgeTaken = (frameList || []).some(
    (f) => f.FrameId !== frame?.FrameId && String(f.IwoNo) === String(form.iwoNo) && f.ComponentName === "EDGE_GRINDING"
  );

  const componentOptions = useMemo(() => {
    const list = [...(components || [])];
    if (form.componentName && !list.some((c) => c.value === form.componentName)) {
      list.push({ value: form.componentName, label: form.componentName.replace(/_/g, " ") });
    }
    return list;
  }, [components, form.componentName]);

  /* ---- parts ---- */
  const updatePart = (key, value) =>
    setForm((prev) => ({ ...prev, parts: prev.parts.map((p) => (p.key === key ? { ...p, partNo: value } : p)) }));
  const addPart = () =>
    setForm((prev) => ({ ...prev, parts: [...prev.parts, { key: `new-${++newPartSeq}`, partId: "", partNo: "", original: "" }] }));
  // new part -> taken out of the list; saved part -> marked removed (can be restored)
  const removePart = (key) =>
    setForm((prev) => ({
      ...prev,
      parts: prev.parts
        .filter((p) => !(p.key === key && !p.partId))
        .map((p) => (p.key === key ? { ...p, removed: true } : p)),
    }));
  const restorePart = (key) =>
    setForm((prev) => ({ ...prev, parts: prev.parts.map((p) => (p.key === key ? { ...p, removed: false, partNo: p.original } : p)) }));

  /* ---- quick add: type "300, 301 302" and press Enter (no mouse needed) ---- */
  const [quickParts, setQuickParts] = useState("");
  const [quickMsg, setQuickMsg] = useState("");
  const quickRef = useRef(null);
  const addQuickParts = () => {
    const typed = String(quickParts)
      .split(/[\s,;]+/)
      .map((x) => x.trim())
      .filter(Boolean);
    if (typed.length === 0) return;
    const have = new Set(form.parts.filter((p) => !p.removed).map((p) => p.partNo.trim()).filter(Boolean));
    const fresh = Array.from(new Set(typed)).filter((pn) => !have.has(pn));
    const skipped = typed.length - fresh.length;
    if (fresh.length) {
      setForm((prev) => ({
        ...prev,
        parts: [
          ...prev.parts.filter((p) => p.partId || p.partNo.trim()), // drop empty new boxes
          ...fresh.map((pn) => ({ key: `new-${++newPartSeq}`, partId: "", partNo: pn, original: "" })),
        ],
      }));
    }
    setQuickParts("");
    setQuickMsg(
      fresh.length
        ? `${fresh.length} part(s) added${skipped ? ` · ${skipped} already in the list` : ""}`
        : "Already in the list"
    );
  };

  /* ---- what changed (only these go to the API) ---- */
  const changes = useMemo(() => {
    const c = {};
    ["iwoNo", "componentName", "frameNo", "startDate", "endDate"].forEach((k) => {
      const value = k === "frameNo" ? normalizeFrameNo(form[k]) : String(form[k]).trim();
      if (value !== String(original[k]).trim()) c[k] = value;
    });
    // parts: when anything changed (add / remove / swap) send the FULL final list as partNos.
    // parts untouched -> no partNos at all
    const finalParts = Array.from(new Set(form.parts.filter((p) => !p.removed).map((p) => p.partNo.trim()).filter(Boolean)));
    const savedParts = Array.from(new Set(form.parts.filter((p) => p.partId).map((p) => p.original.trim()).filter(Boolean)));
    const sameParts =
      finalParts.length === savedParts.length && [...finalParts].sort().join("\u0001") === [...savedParts].sort().join("\u0001");
    if (!sameParts) c.partNos = finalParts;
    // calculated weight goes as a number when it differs from the saved one (or none was saved yet)
    const w = autoWeight.weight;
    const oldW = String(original.weight).trim();
    if (w !== "" && (oldW === "" || Number(w) !== Number(oldW))) c.weight = Number(w);
    return c;
  }, [form, original, autoWeight]);

  const hasChanges = Object.keys(changes).length > 0;

  /* ---- validation ---- */
  const errors = useMemo(() => {
    const e = {};
    if (!form.iwoNo) e.iwoNo = "Required";
    if (!form.componentName) e.componentName = "Required";
    else if (form.componentName === "EDGE_GRINDING" && edgeTaken) e.componentName = "EDGE Grinding already added for this IWO";
    // Frame / OCL No is optional for EDGE Grinding (still checked when entered)
    if (!form.frameNo.trim()) {
      if (form.componentName !== "EDGE_GRINDING") e.frameNo = "Required";
    } else if (!isValidFrameNo(form.frameNo)) e.frameNo = FRAME_NO_HINT;
    if (!isValidDateText(form.startDate)) e.startDate = form.startDate ? "Invalid date" : "Required";
    if (!isValidDateText(form.endDate)) e.endDate = form.endDate ? "Invalid date" : "Required";
    if (!e.startDate && !e.endDate && form.startDate > form.endDate) e.endDate = "Before start date";
    // weight is calculated from the parts (read only) - nothing to check

    const seen = {};
    const active = form.parts.filter((p) => !p.removed);
    if (form.parts.some((p) => p.partId) && !active.some((p) => p.partNo.trim())) e.parts = "Keep at least one part";
    active.forEach((p) => {
      const v = p.partNo.trim();
      if (p.partId && !v) e[p.key] = "Required";
      if (v) {
        if (seen[v]) e[p.key] = "Duplicate";
        seen[v] = true;
      }
    });
    return e;
  }, [form, edgeTaken]);

  const err = (k) => (showErrors ? errors[k] : "");

  const handleSave = async () => {
    setShowErrors(true);
    setError("");
    if (Object.keys(errors).length || !hasChanges) return;

    const result = await dispatch(
      updatePmFrame({
        frameId: String(frame.FrameId),
        ...changes,
        updatedBy: localStorage.getItem("employeeId") || "",
      })
    );

    if (result.success) {
      onSaved && onSaved(`${changes.frameNo || frame.FrameNo} updated`);
    } else {
      setError(result.message);
    }
  };

  const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "10px" } };
  const changedSx = (k) => (changes[k] !== undefined ? { "& .MuiOutlinedInput-root": { borderRadius: "10px", backgroundColor: "#fff8e6" } } : fieldSx);

  if (!frame) return null;

  return (
    <Dialog
      open={open}
      onClose={frameUpdateLoading ? undefined : onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : "16px" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, color: NAVY, fontWeight: 700, pr: 1 }}>
        Edit {frame.FrameNo}
        <Chip size="small" label={`ID ${frame.FrameId}`} sx={{ fontSize: "11px", height: 20 }} />
        <IconButton onClick={onClose} disabled={frameUpdateLoading} sx={{ ml: "auto" }} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
          <Autocomplete
            options={iwoOptions}
            value={iwoOptions.find((x) => String(x.IwoNo) === form.iwoNo) || null}
            onChange={(_, v) => set("iwoNo", v ? String(v.IwoNo) : "")}
            getOptionLabel={(o) => (o.EngSpec ? `${o.IwoNo} - ${o.EngSpec}` : String(o.IwoNo))}
            isOptionEqualToValue={(a, b) => String(a.IwoNo) === String(b.IwoNo)}
            sx={{ gridColumn: { sm: "1 / -1" } }}
            renderInput={(params) => (
              <TextField {...params} label="IWO No" error={!!err("iwoNo")} helperText={err("iwoNo")} sx={changedSx("iwoNo")} />
            )}
          />

          <TextField
            select
            label="Component"
            value={form.componentName}
            onChange={(e) => set("componentName", e.target.value)}
            error={!!err("componentName")}
            helperText={err("componentName")}
            sx={changedSx("componentName")}
          >
            {componentOptions.map((c) => {
              // EDGE Grinding: only one per IWO (shown, but can't be picked if another frame of this IWO has it)
              const blocked = c.value === "EDGE_GRINDING" && form.componentName !== "EDGE_GRINDING" && edgeTaken;
              return (
                <MenuItem key={c.value} value={c.value} disabled={blocked}>
                  {blocked ? `${c.label} (added)` : c.label}
                </MenuItem>
              );
            })}
          </TextField>

          <Box>
            <TextField
              fullWidth
              label="Frame / OCL No"
              placeholder={form.componentName === "EDGE_GRINDING" ? "Optional" : "Frame -24 / OCL 700"}
              value={form.frameNo}
              onChange={(e) => {
                const next = applyFrameNoTyping(form.frameNo, e.target.value);
                if (next === null) return; // only Frame.. / OCL.. can be typed
                set("frameNo", next);
              }}
              error={!!err("frameNo")}
              helperText={err("frameNo")}
              sx={changedSx("frameNo")}
            />
            {/* "Frame -24" typed -> pick the side (or type P / S / C) */}
            {needsFrameSide(form.frameNo) && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75 }}>
                {FRAME_SIDES.map((s) => (
                  <Chip
                    key={s.key}
                    size="small"
                    clickable
                    label={`${s.key.toUpperCase()} · ${s.label}`}
                    onClick={() => set("frameNo", withFrameSide(form.frameNo, s.label))}
                    sx={{ fontSize: "11px", backgroundColor: "#e8f0fa", color: NAVY, fontWeight: 600 }}
                  />
                ))}
              </Box>
            )}
          </Box>

          <TextField
            label="Start Date"
            placeholder="yyyy-mm-dd"
            value={form.startDate}
            onChange={(e) => set("startDate", formatDateTyping(e.target.value))}
            inputProps={{ inputMode: "numeric", maxLength: 10 }}
            InputLabelProps={{ shrink: true }}
            error={!!err("startDate")}
            helperText={err("startDate")}
            sx={changedSx("startDate")}
          />

          <TextField
            label="End Date"
            placeholder="yyyy-mm-dd"
            value={form.endDate}
            onChange={(e) => set("endDate", formatDateTyping(e.target.value))}
            inputProps={{ inputMode: "numeric", maxLength: 10 }}
            InputLabelProps={{ shrink: true }}
            error={!!err("endDate")}
            helperText={err("endDate")}
            sx={changedSx("endDate")}
          />

          <TextField
            label="Weight (auto)"
            placeholder="auto"
            value={shownWeight}
            inputProps={{ readOnly: true, tabIndex: -1, "aria-label": "Weight" }}
            InputProps={{
              endAdornment: partWeightsLoading ? (
                <CircularProgress size={16} />
              ) : (
                <Typography sx={{ fontSize: "13px", color: "#6b7280" }}>kg</Typography>
              ),
            }}
            InputLabelProps={{ shrink: true }}
            helperText={
              partWeightsLoading
                ? "Calculating…"
                : autoWeight.total === 0
                ? "Add part nos to calculate"
                : autoWeight.found === 0
                ? "No part weights for this IWO"
                : autoWeight.found === autoWeight.total
                ? `Sum of ${autoWeight.total} part weight(s)${changes.weight !== undefined && String(original.weight).trim() !== "" ? ` · was ${original.weight}` : ""}`
                : `${autoWeight.found}/${autoWeight.total} parts have weight`
            }
            FormHelperTextProps={{ sx: { color: autoWeight.found > 0 && autoWeight.found < autoWeight.total ? "#d97706" : undefined } }}
            sx={{
              ...changedSx("weight"),
              "& .MuiInputBase-input": { fontWeight: 700, color: "#0E3C66", cursor: "default" },
              ...(changes.weight === undefined ? { "& .MuiOutlinedInput-root": { borderRadius: "10px", backgroundColor: "#f3f6fa" } } : {}),
            }}
          />
        </Box>

        {/* Parts */}
        <Box sx={{ mt: 2.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: "14px" }}>Part Nos</Typography>
            <Typography sx={{ fontSize: "12px", color: "#6b7280", ml: 1 }}>({form.parts.filter((p) => !p.removed).length})</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={addPart} tabIndex={-1} sx={{ ml: "auto", textTransform: "none", fontWeight: 600 }}>
              Add part
            </Button>
          </Box>
          {err("parts") && (
            <Typography sx={{ fontSize: "12px", color: "#d32f2f", mb: 1 }}>{err("parts")}</Typography>
          )}

          {/* quick add: many parts at once, Enter to add */}
          <TextField
            inputRef={quickRef}
            size="small"
            fullWidth
            label="Add part nos"
            placeholder="e.g. 300, 301 302 - press Enter"
            value={quickParts}
            onChange={(e) => {
              setQuickParts(e.target.value);
              setQuickMsg("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addQuickParts();
              }
            }}
            onPaste={(e) => {
              // a column copied from Excel comes with new lines -> keep them as commas
              const text = e.clipboardData.getData("text");
              if (/[\r\n\t]/.test(text)) {
                e.preventDefault();
                const list = text.split(/[\r\n\t]+/).map((x) => x.trim()).filter(Boolean).join(", ");
                setQuickParts((prev) => (prev.trim() ? `${prev.trim()}, ${list}` : list));
                setQuickMsg("");
              }
            }}
            InputProps={{
              endAdornment: quickParts.trim() ? (
                <Button size="small" onClick={addQuickParts} sx={{ textTransform: "none", fontWeight: 700, minWidth: 0 }}>
                  Add
                </Button>
              ) : null,
            }}
            InputLabelProps={{ shrink: true }}
            helperText={quickMsg || "Separate with comma or space · Enter adds them"}
            FormHelperTextProps={{ sx: { color: quickMsg ? "#2e7d32" : undefined, mx: 0.5 } }}
            sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
          />

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr" }, gap: 1.25 }}>
            {form.parts.map((p, i) => {
              const isNew = !p.partId;
              const changed = !isNew && p.partNo.trim() !== p.original;
              if (p.removed) {
                return (
                  <TextField
                    key={p.key}
                    size="small"
                    label={`Part ${i + 1}`}
                    value={p.original}
                    disabled
                    helperText="Removed"
                    inputProps={{ "aria-label": `Removed part no ${p.original}` }}
                    InputProps={{
                      endAdornment: (
                        <Tooltip title="Restore">
                          <IconButton size="small" edge="end" onClick={() => restorePart(p.key)} aria-label={`Restore part ${p.original}`}>
                            <UndoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "10px", backgroundColor: "#fdecec" },
                      "& .MuiInputBase-input": { textDecoration: "line-through" },
                      "& .MuiFormHelperText-root": { mx: 0.5, fontSize: "11px", color: "#c62828" },
                    }}
                  />
                );
              }
              return (
                <TextField
                  key={p.key}
                  size="small"
                  label={isNew ? "New part" : `Part ${i + 1}`}
                  value={p.partNo}
                  onChange={(e) => updatePart(p.key, e.target.value)}
                  onKeyDown={(e) => {
                    // Enter -> next part box (last one -> the quick add box)
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const inputs = Array.from(document.querySelectorAll('[data-part-input="1"]'));
                      const next = inputs[inputs.indexOf(e.target) + 1];
                      (next || quickRef.current)?.focus();
                    }
                  }}
                  error={!!err(p.key)}
                  helperText={err(p.key) || (changed ? `was ${p.original}` : " ")}
                  inputProps={{ "aria-label": isNew ? "New part no" : `Part no ${p.original}`, "data-part-input": "1" }}
                  InputProps={{
                    endAdornment: changed ? (
                      <Tooltip title="Undo">
                        <IconButton size="small" edge="end" onClick={() => updatePart(p.key, p.original)} aria-label="Undo part change">
                          <UndoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Remove">
                        <IconButton size="small" edge="end" tabIndex={-1} onClick={() => removePart(p.key)} aria-label={isNew ? "Remove new part" : `Remove part ${p.original}`}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      backgroundColor: isNew ? "#eef7ee" : changed ? "#fff8e6" : "#fff",
                    },
                    "& .MuiFormHelperText-root": { mx: 0.5, fontSize: "11px" },
                  }}
                />
              );
            })}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: "10px" }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
        <Typography sx={{ fontSize: "12px", color: "#6b7280", mr: "auto" }}>
          {hasChanges ? `${Object.keys(changes).length} change(s)` : "No changes"}
        </Typography>
        <Button onClick={onClose} disabled={frameUpdateLoading} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!hasChanges || frameUpdateLoading}
          startIcon={frameUpdateLoading ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", backgroundColor: NAVY }}
        >
          Update Frame
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditFrameDialog;